import type { InstancePresence, ProfileState, ReceivedSnapshot } from '@apm/contracts';
import { heapBasisBytes, heapBasisIsLimit, heapUsedRatio } from '@apm/core';
import { routeHref } from '../router';
import { MSG, TIP } from '../terms';
import { formatBytes, formatCount, formatFull, formatMs, formatRelative } from '../time';
import { Chip, EmptyState, Lines, Tooltip } from '../ui';
import { type ErrorCounts, instanceKey } from './errors';

/**
 * 프로세스별 게이지. 처리 지연·메모리·DB 연결·초당 완료/오류.
 *
 * 16회차에 "최근 5분 오류" 배지가 붙었다. 초당 오류(1초 스냅샷)는 눈을 떼면 사라지는데,
 * 카드에서 알고 싶은 것은 "이 프로세스가 지금 아픈가"다. 그 판정은 끝난 요청 5분 창에서 온다.
 *
 * 19회차에 마지막 보고 시각과 끊긴 프로세스가 붙었다(콘솔 검토 C-6). 값 옆의 경고는
 * 색만 바꾸지 않고 글자를 함께 낸다(D-3).
 * 22-C회차에 프로세스 시작 시각과 에이전트 버전이 붙었다(UI 피드백 9). 옛 에이전트는 이 값을
 * 보내지 않으므로 없으면 줄을 그리지 않는다. X-View의 재시작 세로선은 다음 콘솔 회차 몫이다.
 *
 * 36회차에 .NET 런타임 칸(`dotnet`)이 붙었다. 규칙은 29회차 `jvm`과 같다 - **없는 값의 칸은
 * 아예 안 그린다.** 0을 그리면 "스레드가 하나도 안 남았다"는 거짓말이 되기 때문이다.
 * GC 정지 시간이 근사인 런타임(.NET Framework 4.8)에서는 그 칸에 "(근사)"를 글자로 붙인다.
 * **보내는 쪽은 아직 없다** - 지금 붙어 있는 .NET 송신기는 공통 값만 보내므로 이 칸들은
 * 후속 송신기 판이 나오기 전까지 그려지지 않는다.
 */

/** "시작 3시간 전 · 에이전트 0.1.0". 시작 시각이 없으면 아무것도 없다 */
function startedLine(snapshot: Pick<ReceivedSnapshot, 'processStartedAt' | 'agentVersion'>) {
  if (snapshot.processStartedAt === undefined) return null;
  const version = snapshot.agentVersion === undefined ? '' : ` · 에이전트 ${snapshot.agentVersion}`;
  return (
    <p className="mt-0.5 text-xs text-muted" title={formatFull(snapshot.processStartedAt)}>
      <Tooltip text={TIP.processStarted}>
        시작 {formatRelative(snapshot.processStartedAt)}
        {version}
      </Tooltip>
    </p>
  );
}
export function Gauges({
  instances,
  silent = [],
  errors,
  grouped = false,
  presence = [],
}: {
  instances: readonly ReceivedSnapshot[];
  /** 보고가 끊긴 프로세스의 마지막 값 */
  silent?: readonly ReceivedSnapshot[];
  /** 프로세스 키 → 최근 5분 오류 수. 없으면 배지를 그리지 않는다 */
  errors?: ReadonlyMap<string, ErrorCounts>;
  /** 여러 서비스를 함께 볼 때 서비스 이름 아래에 인스턴스 카드를 모은다 */
  grouped?: boolean;
  presence?: readonly InstancePresence[];
}) {
  if (instances.length === 0 && silent.length === 0 && presence.length === 0) {
    return <EmptyState title={MSG.noAgent} hint={MSG.noAgentHint} />;
  }
  if (grouped) {
    const services = [
      ...new Set([...instances, ...silent, ...presence].map((item) => item.service)),
    ].sort();
    return (
      <div className="space-y-5">
        {services.map((service) => (
          <section key={service} aria-label={`${service} 실시간 지표`}>
            <h3 className="mb-2 text-sm font-semibold text-ink">{service}</h3>
            <GaugeGrid
              instances={instances.filter((item) => item.service === service)}
              silent={silent.filter((item) => item.service === service)}
              errors={errors}
              presence={presence.filter((item) => item.service === service)}
              showService={false}
            />
          </section>
        ))}
      </div>
    );
  }
  return (
    <GaugeGrid
      instances={instances}
      silent={silent}
      errors={errors}
      presence={presence}
      showService
    />
  );
}

function GaugeGrid({
  instances,
  silent,
  errors,
  presence,
  showService,
}: {
  instances: readonly ReceivedSnapshot[];
  silent: readonly ReceivedSnapshot[];
  errors?: ReadonlyMap<string, ErrorCounts>;
  presence: readonly InstancePresence[];
  showService: boolean;
}) {
  const presenceByKey = new Map(
    presence.map((item) => [instanceKey(item.service, item.instance ?? ''), item]),
  );
  const snapshots = new Set(
    [...instances, ...silent].map((item) => instanceKey(item.service, item.instance)),
  );
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      {instances.map((snapshot) => {
        const key = instanceKey(snapshot.service, snapshot.instance);
        const status = presenceByKey.get(key);
        return status?.stale ? (
          <StaleCard key={key} snapshot={snapshot} presence={status} showService={showService} />
        ) : (
          <InstanceCard
            key={key}
            snapshot={snapshot}
            presence={status}
            errors={errors?.get(key)}
            showService={showService}
          />
        );
      })}
      {silent.map((snapshot) => (
        <StaleCard
          key={`silent-${instanceKey(snapshot.service, snapshot.instance)}`}
          snapshot={snapshot}
          presence={presenceByKey.get(instanceKey(snapshot.service, snapshot.instance))}
          showService={showService}
        />
      ))}
      {presence
        .filter((item) => !snapshots.has(instanceKey(item.service, item.instance ?? '')))
        .map((item) => (
          <PresenceOnlyCard key={instanceKey(item.service, item.instance ?? '')} presence={item} />
        ))}
    </div>
  );
}

/** 처리 지연은 카드 오른쪽에 크게 띄운다. 한눈에 볼 값은 이것 하나다 */
function loopLevel(p99: number): 'normal' | 'warn' | 'danger' {
  if (p99 >= 50) return 'danger';
  if (p99 >= 10) return 'warn';
  return 'normal';
}

const LOOP_TONE = {
  normal: 'text-ok',
  warn: 'text-fail',
  danger: 'text-error',
} as const;

const LOOP_NOTE = {
  normal: '',
  warn: '지연',
  danger: '심한 지연',
} as const;

/** CPU 기록 상태 배지 (8회차 결정 4). 감시 부품이 상태를 안 보내면 아예 그리지 않는다 */
const PROFILE_BADGE: Record<ProfileState['state'], string> = {
  running: 'CPU 기록 중',
  cooldown: 'CPU 기록 쿨다운',
  idle: 'CPU 기록 대기',
};

function ProfileBadge({ profile }: { profile: ProfileState }) {
  return (
    <a
      href={routeHref({ name: 'diagnose', sub: 'cpu' })}
      title={
        profile.lastStartedAt === undefined
          ? '아직 CPU 기록이 없다'
          : `마지막 기록 ${formatFull(profile.lastStartedAt)}`
      }
      className="no-underline"
    >
      <Chip tone={profile.state === 'running' ? 'error' : 'neutral'}>
        {PROFILE_BADGE[profile.state]}
        {profile.count > 0 ? ` ${profile.count}건` : ''}
      </Chip>
    </a>
  );
}

/**
 * 최근 5분 오류 배지 (16회차). 오류가 하나도 없으면 아예 그리지 않는다.
 * 카드에 늘 떠 있는 "0건"은 읽히지 않고 자리만 먹는다.
 */
function ErrorBadge({ errors }: { errors: ErrorCounts }) {
  if (errors.errors === 0 && errors.fails === 0) return null;
  const parts = [
    errors.errors > 0 ? `오류 ${errors.errors}건` : '',
    errors.fails > 0 ? `실패 ${errors.fails}건` : '',
  ].filter((part) => part !== '');
  return (
    <Chip
      tone={errors.errors > 0 ? 'error' : 'fail'}
      title="최근 5분에 끝난 요청 기준. 실시간 연결은 빼고 센다"
    >
      최근 5분 {parts.join(' · ')}
    </Chip>
  );
}

function InstanceCard({
  snapshot,
  errors,
  showService,
  presence,
}: {
  snapshot: ReceivedSnapshot;
  errors?: ErrorCounts;
  showService: boolean;
  presence?: InstancePresence;
}) {
  const { loop, heap, pool, rate, gc, jvm, dotnet } = snapshot;
  // 분모는 V8 힙 상한(22-C회차). 확보한 힙 대비는 늘 80~97%라 "90% 초과"가 헛경보였다.
  // 상한을 안 보내는 옛 에이전트는 예전 기준으로 두되 그 사실을 적는다
  const heapRatio = heapUsedRatio(heap);
  const heapByLimit = heapBasisIsLimit(heap);
  // 이벤트루프가 없는 런타임(JVM 등)은 이 값이 아예 없다. 0으로 그리면 "안 밀린다"고
  // 말하는 셈이라 자리를 비운다 (29회차)
  const level = loopLevel(loop?.p99 ?? 0);

  return (
    <article className="rounded-xl border border-hair bg-panel p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          {showService ? (
            <h3 className="truncate font-medium text-ink" title={snapshot.service}>
              {snapshot.service}
            </h3>
          ) : null}
          <p className="mt-0.5 truncate font-mono text-xs text-muted" title={snapshot.instance}>
            {snapshot.instance}
          </p>
          <p className="mt-1 text-xs text-muted" title={formatFull(snapshot.ts)}>
            마지막 보고 {formatRelative(snapshot.ts)}
          </p>
          {startedLine(snapshot)}
          {snapshot.profile || errors ? (
            <p className="mt-2 flex flex-wrap items-center gap-1.5">
              {errors ? <ErrorBadge errors={errors} /> : null}
              {snapshot.profile ? <ProfileBadge profile={snapshot.profile} /> : null}
            </p>
          ) : null}
          <PresenceBadges presence={presence} />
        </div>
        {loop ? (
          <div className="shrink-0 text-right">
            <p className="text-xs text-muted">
              <Tooltip text={TIP.loopDelay}>처리 지연(상위 1%)</Tooltip>
            </p>
            <p className={`tabular text-lg font-semibold ${LOOP_TONE[level]}`}>
              {formatMs(loop.p99)}
            </p>
            {LOOP_NOTE[level] ? (
              <p className={`text-xs ${LOOP_TONE[level]}`}>{LOOP_NOTE[level]}</p>
            ) : null}
          </div>
        ) : null}
      </div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
        {loop ? (
          <Metric
            label="처리 지연(최대)"
            tip={TIP.loopMax}
            value={formatMs(loop.max)}
            warn={loop.max >= 50 ? '기준 초과' : undefined}
          />
        ) : null}
        {jvm ? (
          <Metric
            label="스레드"
            value={`${formatCount(jvm.threads.live)}개 · 최대 ${formatCount(jvm.threads.peak)}`}
          />
        ) : null}
        {jvm ? (
          <Metric label="로드된 클래스" value={`${formatCount(jvm.classesLoaded)}개`} />
        ) : null}
        {dotnet ? (
          <Metric
            label="스레드풀(작업)"
            tip={TIP.dotnetThreadPool}
            value={`가용 ${formatCount(dotnet.threadPool.worker.available)}/${formatCount(dotnet.threadPool.worker.max)}`}
            warn={dotnet.threadPool.worker.available === 0 ? '스레드 고갈' : undefined}
          />
        ) : null}
        {dotnet ? (
          <Metric
            label="스레드풀(입출력)"
            tip={TIP.dotnetThreadPool}
            value={`가용 ${formatCount(dotnet.threadPool.io.available)}/${formatCount(dotnet.threadPool.io.max)}`}
            warn={dotnet.threadPool.io.available === 0 ? '스레드 고갈' : undefined}
          />
        ) : null}
        {dotnet?.cpuPercent === undefined ? null : (
          <Metric
            label="CPU 사용률"
            tip={TIP.cpuPercent}
            value={`${Math.round(dotnet.cpuPercent)}%`}
          />
        )}
        <Metric
          label="메모리 정리"
          tip={TIP.gcCount}
          value={`${gc.count}회 ${formatMs(gc.totalMs)}${dotnet?.gcApproximate ? ' (근사)' : ''}`}
        />
        <Metric label="프로세스 메모리" tip={TIP.rss} value={formatBytes(heap.rssBytes)} />
        <Metric
          label="DB 커넥션 풀"
          tip={TIP.pool}
          value={
            pool ? `${pool.total - pool.idle}/${pool.total} · 대기 ${pool.waiting}` : '값 없음'
          }
          note={
            pool === undefined
              ? '풀 지표를 받지 못했다'
              : pool.total === 0
                ? '관측된 연결 0개'
                : undefined
          }
          warn={(pool?.waiting ?? 0) > 0 ? '풀 부족' : undefined}
        />
        <Metric label="초당 완료" tip={TIP.completed} value={`${formatCount(rate.completed)}건`} />
        <Metric
          label="초당 오류"
          value={`${rate.errors}건`}
          warn={rate.errors > 0 ? '오류 발생' : undefined}
        />
      </dl>

      <div className="mt-4">
        <div className="flex items-baseline justify-between text-xs">
          <span className="text-muted">
            <Tooltip text={TIP.heap}>메모리 사용량</Tooltip>
          </span>
          <span className="tabular text-ink">
            {formatBytes(heap.usedBytes)} / {heapByLimit ? '상한 ' : ''}
            {formatBytes(heapBasisBytes(heap))} · {Math.round(heapRatio * 100)}%
            {heapByLimit ? '' : ` ${MSG.heapBasisTotal}`}
            {heapRatio >= 0.9 ? <span className="ml-1.5 text-error">90% 초과</span> : null}
          </span>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-band-strong">
          <div
            className={`h-full rounded-full ${heapRatio >= 0.9 ? 'bg-error' : 'bg-accent'}`}
            style={{ width: `${Math.min(100, heapRatio * 100)}%` }}
          />
        </div>
      </div>
    </article>
  );
}

/** 보고가 끊긴 프로세스. 값은 마지막으로 받은 것이라 회색으로 눌러 둔다 (콘솔 검토 C-6) */
function StaleCard({
  snapshot,
  presence,
  showService,
}: {
  snapshot: ReceivedSnapshot;
  presence?: InstancePresence;
  showService: boolean;
}) {
  const lastSeenAt = presence?.lastSeenAt ?? snapshot.ts;
  return (
    <article className="rounded-xl border border-hair bg-band p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {showService ? (
            <h3 className="truncate font-medium text-muted" title={snapshot.service}>
              {snapshot.service}
            </h3>
          ) : null}
          <p className="mt-0.5 truncate font-mono text-xs text-muted" title={snapshot.instance}>
            {snapshot.instance}
          </p>
          <p className="mt-1 text-xs text-muted" title={formatFull(lastSeenAt)}>
            {formatRelative(lastSeenAt)} 마지막 보고
          </p>
          {startedLine(snapshot)}
          <PresenceBadges presence={presence} />
        </div>
        <Chip tone="fail">보고 끊김</Chip>
      </div>
      <p className="mt-3 text-sm text-muted">
        <Lines
          lines={[
            '이 인스턴스에서 보고가 오지 않는다.',
            '프로세스가 내려갔거나, 에이전트가 멈췄거나, 관제 서버로 가는 네트워크가 막힌 것이다',
          ]}
        />
      </p>
    </article>
  );
}

function PresenceOnlyCard({ presence }: { presence: InstancePresence }) {
  return (
    <article className="rounded-xl border border-hair bg-band p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-mono text-xs text-muted" title={presence.instance}>
            {presence.instance ?? '인스턴스 이름 없음'}
          </p>
          <p className="mt-1 text-xs text-muted" title={formatFull(presence.lastSeenAt)}>
            {formatRelative(presence.lastSeenAt)} 마지막 보고
          </p>
          <PresenceBadges presence={presence} />
        </div>
        <Chip tone={presence.stale ? 'fail' : 'neutral'}>
          {presence.realtime ? '실시간 값 없음' : '트레이스만'}
        </Chip>
      </div>
    </article>
  );
}

function PresenceBadges({ presence }: { presence?: InstancePresence }) {
  if (presence?.restartedAt === undefined && presence?.conflict !== true) return null;
  return (
    <p className="mt-2 flex flex-wrap gap-1.5">
      {presence.restartedAt === undefined ? null : (
        <Chip tone="neutral" title={formatFull(presence.restartedAt)}>
          재시작 {formatRelative(presence.restartedAt)}
        </Chip>
      )}
      {presence.conflict ? (
        <Chip tone="error" title="같은 이름으로 여러 프로세스가 번갈아 보고하고 있다">
          인스턴스 이름 충돌
        </Chip>
      ) : null}
    </p>
  );
}

function Metric({
  label,
  value,
  tip,
  warn,
  note,
}: {
  label: string;
  value: string;
  tip?: string;
  warn?: string;
  note?: string;
}) {
  return (
    <div>
      <dt className="whitespace-nowrap text-xs text-muted">
        {tip ? <Tooltip text={tip}>{label}</Tooltip> : label}
      </dt>
      <dd className={`tabular mt-1 text-sm ${warn ? 'text-fail' : 'text-ink'}`}>
        {value}
        {warn ? <span className="ml-1.5 text-xs">{warn}</span> : null}
      </dd>
      {note ? <dd className="mt-0.5 text-xs text-muted">{note}</dd> : null}
    </div>
  );
}
